// Shared helpers for the password-entry forms (change-password and password-setup) that render
// a live password-strength indicator and a Yup-backed validation flow. Extracted here because both
// components previously duplicated this logic verbatim - behavior/output is unchanged.

/** Special characters accepted by the server, shown in the password guidelines. */
export const PASSWORD_SPECIAL_CHARACTERS = [
    '!',
    '@',
    '#',
    '$',
    '%',
    '^',
    '&',
    '*',
    '(',
    ')',
    ',',
    '.',
    '?',
    '"',
    ':',
    '{',
    '}',
    '|',
    '<',
    '>',
    '-',
    '_',
];

/** Matches one of the allowed special characters (kept in sync with PASSWORD_SPECIAL_CHARACTERS). */
export const PASSWORD_SPECIAL_CHARACTER_REGEX = /[!@#$%^&*(),.?":{}|<>_-]/;

/** The five password-strength checks shared by change-password and password-setup. */
export const getPasswordStrengthChecks = (password: string): boolean[] => [
    password.length >= 8,
    /[a-z]/.test(password),
    /[A-Z]/.test(password),
    /[0-9]/.test(password),
    PASSWORD_SPECIAL_CHARACTER_REGEX.test(password),
];

/** Number (0-5) of password-strength checks satisfied by the given password. */
export const computePasswordStrength = (password: string): number => {
    return getPasswordStrengthChecks(password).filter(Boolean).length;
};

/** Human-readable password-strength summary message, identical across both password forms. */
export const getPasswordStrengthMessage = (password: string): string => {
    if (!password || password.length === 0) {
        return 'Enter a new password to see its strength.';
    }
    const strength = computePasswordStrength(password);

    if (strength <= 2) {
        return 'Weak Password. Please ensure your password meets the required criteria.';
    }

    if (strength <= 4) {
        return 'Medium Password. Please ensure your password meets the required criteria.';
    }

    return 'Good choice. Your password meets all requirements.';
};

/** Maps a Yup `ValidationError` (with `abortEarly: false`) into a field-name -> message record. */
export const mapYupValidationErrors = (error: unknown): Record<string, string> => {
    const yupError = error as { inner: { path: string; message: string }[] };
    const errors: Record<string, string> = {};
    yupError.inner.forEach(err => {
        errors[err.path] = err.message;
    });
    return errors;
};
